<template>
    <v-dialog :modelValue="modelValue" @update:modelValue="syncVModel" max-width="800">
        <v-card :min-width="600">
            <v-card-title>
                <h3>Add Shared Vessel</h3>
            </v-card-title>
            <v-card-text>
                <v-container>
                    <v-row class="no-gutters">
                        <v-col>
                            <v-select label="Role" v-model="accountRole" :items="availableAccountRoles" hide-details></v-select>
                        </v-col>
                    </v-row>
                    <v-row class="no-gutters">
                        <v-col>
                            <v-text-field
                                :disabled="!canSearchForVessel"
                                v-model="vesselSearchTerm"
                                label="Search for Vessel"
                                hide-details
                                prepend-inner-icon="mdi-magnify"
                                :loading="searchingForVessels"
                                placeholder="Search for a vessel to attach"
                                clearable
                                hint="Search by name, address, or vessel ID"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-list v-if="matchingVessels && matchingVessels.length > 0">
                                <VesselSearchResult
                                    v-for="vessel of matchingVessels"
                                    :key="vessel.vesselId"
                                    :vessel="vessel"
                                    :searchTerm="vesselSearchTerm"
                                    @click="addVessel(vessel)"
                                />
                            </v-list>
                            <p v-else-if="!searchingForVessels && validVesselSearchTerm?.length">
                                No matching vessel found for '{{ validVesselSearchTerm }}'. Search by name, location, or vessel ID
                            </p>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
            <v-card-actions>
                <v-btn color="gray-darken-1" fill="clear" @click="syncVModel(false)">Cancel</v-btn>
                <v-spacer></v-spacer>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts" setup>
import { SharedVesselRolesV1, type VesselSearchResultV1 } from '@geckoal/gecko-api-client'
import { debounce } from '@geckoal/gecko-webapp-utils'
import { computed, ref, watch } from 'vue'
import { useApi } from '../../api'
import { handleApiError } from '../../lib/utils'
import VesselSearchResult from '../vessels/VesselSearchResult.vue'

const props = defineProps({
    modelValue: {
        type: Boolean
    },
    accountId: {
        type: Number,
        required: true
    }
})

const availableAccountRoles = ref<SharedVesselRolesV1[]>(Object.values(SharedVesselRolesV1))
const accountRole = ref<SharedVesselRolesV1 | undefined>()

const vesselSearchTerm = ref<string | undefined>()
const searchingForVessels = ref(false)
const matchingVessels = ref<VesselSearchResultV1[] | undefined>()

const addingVessel = ref(false)

const emit = defineEmits<{
    (event: 'update:modelValue', value: boolean): void
    (event: 'added-vessel', vessel: VesselSearchResultV1): void
}>()

const syncVModel = (value: boolean) => {
    emit('update:modelValue', value)
    if (value === false) {
        accountRole.value = undefined
        matchingVessels.value = undefined
        vesselSearchTerm.value = undefined
    }
}

const canSearchForVessel = computed(() => {
    return accountRole.value !== undefined
})

const validVesselSearchTerm = computed(() => {
    if (vesselSearchTerm.value === undefined || vesselSearchTerm.value === null || vesselSearchTerm.value?.trim() === '') return undefined
    if (vesselSearchTerm.value.length < 3) return undefined
    return vesselSearchTerm.value
})

watch(
    validVesselSearchTerm,
    debounce(() => {
        if (validVesselSearchTerm.value === undefined) {
            matchingVessels.value = undefined
            return
        }
        performSearch(validVesselSearchTerm.value)
    }, 300)
)

const performSearch = (searchTerm: string) => {
    if (searchTerm.length < 3) return

    handleApiError(
        async () => {
            searchingForVessels.value = true

            const result = await useApi().searchVesselsV1({
                searchVesselsV1: {
                    searchTerm,
                    excludeAccountId: props.accountId
                },
                limit: 10
            })

            matchingVessels.value = result.vessels
        },
        async () => {
            searchingForVessels.value = false
        }
    )
}

const addVessel = async (vessel: VesselSearchResultV1) => {
    const role = accountRole.value
    if (role === undefined) return

    handleApiError(
        async () => {
            addingVessel.value = true

            const result = await useApi().addSharedVesselV1({
                accountId: props.accountId,
                putSharedVesselRequestV1: {
                    vesselId: vessel.vesselId,
                    role
                }
            })
            console.log('Added shared vessel', result)
            emit('added-vessel', vessel)
            syncVModel(false)
        },
        async () => {
            addingVessel.value = false
        }
    )
}
</script>

<style lang="scss" scoped></style>
